import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { SkeletonModule } from "primeng/skeleton";
import { TabsModule } from "primeng/tabs";

@Component({
  selector: "app-customer-portal-skeleton",
  standalone: true,
  imports: [CommonModule, SkeletonModule, TabsModule],
  template: `
    <div class="customer-portal-skeleton">
      <!-- Tabs Skeleton -->
      <div class="tabs-container">
        <!-- Tab List Skeleton -->
        <div class="tab-list-skeleton">
          <p-skeleton height="2.5rem" width="10rem" class="tab-skeleton" />
          <p-skeleton height="2.5rem" width="12rem" class="tab-skeleton" />
          <p-skeleton height="2.5rem" width="10rem" class="tab-skeleton" />
          <p-skeleton height="2.5rem" width="14rem" class="tab-skeleton" />
        </div>

        <!-- Tab Content Skeleton - General Settings Layout -->
        <div class="tab-content-skeleton">
          <!-- General Settings Card Skeleton -->
          <div class="settings-card-skeleton">
            <!-- Card Header -->
            <div class="card-header-skeleton">
              <div class="flex items-center gap-2 mb-2">
                <p-skeleton width="1.5rem" height="1.5rem" borderRadius="50%" />
                <p-skeleton height="1.25rem" width="10rem" />
                <p-skeleton width="1rem" height="1rem" borderRadius="50%" />
              </div>
              <p-skeleton height="0.875rem" width="75%" />
            </div>

            <!-- Card Content -->
            <div class="card-content-skeleton">
              <!-- Section Header -->
              <p-skeleton height="1.1rem" width="12rem" class="mb-4" />

              <!-- Option Items -->
              <div class="option-item-skeleton">
                <p-skeleton
                  width="1.25rem"
                  height="1.25rem"
                  borderRadius="4px"
                />
                <p-skeleton height="1rem" width="85%" />
              </div>

              <div class="option-item-skeleton">
                <p-skeleton
                  width="1.25rem"
                  height="1.25rem"
                  borderRadius="4px"
                />
                <p-skeleton height="1rem" width="80%" />
              </div>

              <div class="option-item-skeleton">
                <p-skeleton
                  width="1.25rem"
                  height="1.25rem"
                  borderRadius="4px"
                />
                <p-skeleton height="1rem" width="78%" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Section Skeleton -->
      <div class="actions-skeleton">
        <div class="actions-card-skeleton">
          <div class="actions-buttons-skeleton">
            <p-skeleton height="2rem" width="8rem" />
            <p-skeleton height="2rem" width="5rem" />
          </div>
          <div class="changes-indicator-skeleton">
            <p-skeleton
              height="1rem"
              width="1rem"
              borderRadius="50%"
              class="mr-2"
            />
            <p-skeleton height="1rem" width="12rem" />
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        background-color: white;
        overflow: hidden;
        min-height: 0;
      }

      .customer-portal-skeleton {
        display: flex;
        flex-direction: column;
        height: 100%; // Changed from calc to 100% to fill all available space
        width: 100%;
        min-height: 0;
        overflow: hidden;
        animation: pulse 2s infinite;
      }

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.8;
        }
      }

      .header-section {
        padding: 0.75rem 1rem;
        flex-shrink: 0;
        border-bottom: 1px solid #f3f4f6;
        background: rgba(255, 255, 255, 0.95);
        margin-bottom: 0.5rem;
      }

      .breadcrumb-title-skeleton {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        min-height: 2.5rem;
      }

      .breadcrumb-skeleton {
        display: flex;
        align-items: center;
        flex: 1;
      }

      .separator-skeleton {
        color: #d1d5db;
        font-size: 0.75rem;
        margin: 0 0.25rem;
      }

      .title-skeleton {
        flex-shrink: 0;
      }

      /* Mobile responsive skeleton */
      @media (max-width: 768px) {
        .breadcrumb-title-skeleton {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
          min-height: auto;
        }

        .title-skeleton {
          order: 1;
          width: 100%;
        }

        .breadcrumb-skeleton {
          order: 2;
          width: 100%;
        }
      }

      .tabs-container {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
        padding: 0 1rem;
      }

      .tab-list-skeleton {
        display: flex;
        gap: 0.5rem;
        padding: 0.5rem 0;
        border-bottom: 1px solid #e2e8f0;
        margin-bottom: 1rem;
        flex-shrink: 0;
      }

      .tab-skeleton {
        border-radius: 8px;
      }

      .tab-content-skeleton {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 1rem;
      }

      .two-column-skeleton {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
        align-items: stretch;
      }

      @media (max-width: 1024px) {
        .two-column-skeleton {
          grid-template-columns: 1fr;
          gap: 1rem;
        }
      }

      .card-skeleton {
        background: white;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
        box-shadow:
          0 1px 3px 0 rgb(0 0 0 / 0.1),
          0 1px 2px -1px rgb(0 0 0 / 0.1);
        overflow: hidden;
        height: fit-content;
      }

      .card-header-skeleton {
        padding: 1.25rem;
        border-bottom: 1px solid #e5e7eb;
        background-color: #fafafa;
      }

      .card-content-skeleton {
        padding: 1.25rem;
      }

      .section-skeleton {
        margin-bottom: 1.5rem;
      }

      .section-skeleton:last-child {
        margin-bottom: 0;
      }

      .divider-skeleton {
        height: 1px;
        background: linear-gradient(
          to right,
          transparent,
          #e5e7eb 20%,
          #e5e7eb 80%,
          transparent
        );
        margin: 1.5rem 0;
        position: relative;
      }

      .divider-skeleton::before {
        content: "";
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 6px;
        height: 6px;
        background: #e5e7eb;
        border-radius: 50%;
      }

      .form-field-skeleton {
        margin-bottom: 1.5rem;
      }

      .input-group-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
      }

      .option-item-skeleton {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        background-color: #f8f9fa;
        margin-bottom: 1rem;
        min-height: 60px;
      }

      .actions-skeleton {
        margin-top: auto;
        padding: 0.5rem 0 0 0;
        flex-shrink: 0;
      }

      .actions-card-skeleton {
        background: white;
        border-radius: 12px;
        padding: 1rem;
        box-shadow:
          0 1px 3px 0 rgb(0 0 0 / 0.1),
          0 1px 2px -1px rgb(0 0 0 / 0.1);
        border: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .changes-indicator-skeleton {
        display: flex;
        align-items: center;
      }

      .actions-buttons-skeleton {
        display: flex;
        gap: 0.75rem;
        align-items: center;
        // Removed margin-left: auto so buttons stay on left
      }

      .mb-2 {
        margin-bottom: 0.5rem;
      }

      .mb-3 {
        margin-bottom: 0.75rem;
      }

      .mb-4 {
        margin-bottom: 1rem;
      }

      .mt-1 {
        margin-top: 0.25rem;
      }

      /* PrimeNG Skeleton overrides for better styling */
      ::ng-deep .p-skeleton {
        background: linear-gradient(
          90deg,
          #f0f0f0 25%,
          #e0e0e0 50%,
          #f0f0f0 75%
        );
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
      }

      @keyframes loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `,
  ],
})
export class CustomerPortalSkeletonComponent {}
